import { useEffect } from 'react'

import type { GetStaticPropsContext, GetStaticPropsResult } from 'next'
import { useSession } from 'next-auth/react'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'

import useSWR from 'swr'

import menuData from '@data/menu.json'

import { getAllCollections, getCollectionProductsPerPage } from '@lib/operations/collection'
import { getCustomerFavorites } from '@lib/operations/customer-favorites'
import type {
	Breadcrumbs,
	CollectionProducts,
	Collection as CollectionType,
	Filters,
	PageInfo
} from '@lib/types'
import { getBreadcrumbs } from '@lib/utils/breadcrumbs'
import { removeEmptyHeadings } from '@lib/utils/helpers'

import PageBreadcrumbs from '@components/global/Breadcrumbs'
import ProductsListContainer from '@components/products/ProductsListContainer'

type Props = {
	collection: CollectionType
	products: CollectionProducts
	filters: Filters
	pageInfo: PageInfo
	breadcrumbs: Breadcrumbs
	redirectTo?: string | null
}

export default function Category({
	collection,
	products,
	filters: filtersOptions,
	pageInfo,
	breadcrumbs,
	redirectTo
}: Props) {
	const router = useRouter()
	const { collectionHandle } = router.query
	const { data: session } = useSession()

	const { data: favorites } = useSWR<string[] | null>(
		[(session as any)?.shopifyAccessToken as string, 'favorites'],
		getCustomerFavorites,
		{ revalidateIfStale: true }
	)

	useEffect(() => {
		if (redirectTo) {
			router.push(redirectTo)
		}
	}, [])

	// hide last breadcrumb item if collection equals it
	const shouldHideLastBreadcrumb = router.asPath === breadcrumbs?.at?.(-1)?.path

	if (redirectTo) return null

	return (
		<>
			<NextSeo title={collection?.seo?.title ?? collection?.name ?? ''} />

			{/* Hero */}
			<header className="relative pt-12 pb-6 lg:pb-16">
				<div className="container">
					{breadcrumbs && (
						<PageBreadcrumbs
							breadcrumbs={breadcrumbs}
							shouldHideLastItem={shouldHideLastBreadcrumb}
						/>
					)}

					{/* Category title */}
					<h1 className="title text-2xl lg:text-4xl title">{collection?.name ?? ''}</h1>

					{collection && collection.descriptionHtml && (
						<div
							className="font-light text-gray-500 max-w-screen-sm mt-4"
							// eslint-disable-next-line react/no-danger
							dangerouslySetInnerHTML={{
								__html: removeEmptyHeadings(collection?.descriptionHtml ?? '')
							}}
						/>
					)}
				</div>
			</header>

			<ProductsListContainer
				collectionSlug={collectionHandle as string}
				products={products}
				breadcrumbs={breadcrumbs}
				pageInfo={pageInfo}
				filtersOptions={filtersOptions}
				favorites={favorites}
			/>
		</>
	)
}

type GetStaticPropsParams = {
	collectionHandle: string
}
export async function getStaticProps({
	params
}: GetStaticPropsContext<GetStaticPropsParams>): Promise<
	GetStaticPropsResult<Props & { key: string }>
> {
	const { collectionHandle } = params as GetStaticPropsParams

	const breadcrumbs = getBreadcrumbs(collectionHandle, menuData.items) as Breadcrumbs

	const queryResponse = await getCollectionProductsPerPage(collectionHandle)
	const { collection, products, filters, pageInfo } = queryResponse ?? {}

	if (!collection || !products || !filters || !pageInfo) {
		return { notFound: true }
	}

	return {
		props: {
			key: collectionHandle,
			collection,
			products,
			filters,
			pageInfo,
			breadcrumbs,
			redirectTo: collectionHandle.includes('member-market') ? '/members-market' : null
		},
		revalidate: 60
	}
}

export async function getStaticPaths() {
	const allCollections = (await getAllCollections()) as CollectionType[]

	const paths = allCollections.map((collection: CollectionType) => ({
		params: {
			collectionHandle: collection.handle
		}
	}))

	return {
		paths,
		fallback: false
	}
}
